/**
 * Root console: partners (affiliates and distributors). Reachable by the root user only, so its UI text is
 * hardcoded English (not i18n) by design. Do not introduce translation keys here.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import { HiRefresh, HiUserGroup } from "react-icons/hi";
import useAuth from "../hooks/useAuth";
import { CustomButton } from "../components/atoms/CustomButton";
import { DataTable } from "../components/atoms/DataTable";
import { EmptyState } from "../components/atoms/EmptyState";
import { Spinner } from "../components/atoms/Spinner";
import { NoTranslate } from "../components/atoms/NoTranslate";
import PartnerAdminService, {
  PartnerProgram,
  PartnerSummary,
} from "../services/partnerAdmin";
import PartnerDrawer from "../components/pages/dashboard/partners/PartnerDrawer";
import UpgradeSettlements from "../components/pages/dashboard/partners/UpgradeSettlements";
import PartnerLinkFailures from "../components/pages/dashboard/partners/PartnerLinkFailures";
import PartnerDemoRequests from "../components/pages/dashboard/partners/PartnerDemoRequests";
import {
  clientCount,
  formatDay,
  partnerName,
  programBadge,
} from "../components/pages/dashboard/partners/partnerLabels";

const FIELD =
  "w-full rounded-md border border-gray-300 bg-white p-2.5 text-base text-gray-900 placeholder-gray-400 focus:border-blue-500 focus:ring-blue-500 md:py-2 md:text-sm";

type ProgramFilter = "all" | PartnerProgram;

const FILTERS: { value: ProgramFilter; label: string }[] = [
  { value: "all", label: "All programs" },
  { value: "distributor", label: "Distributors" },
  { value: "distributor_pending", label: "Distributors, contract pending" },
  { value: "affiliate", label: "Affiliates" },
  { value: "unset", label: "No program yet" },
];

const Partners: React.FC = () => {
  const { customer_identification, user } = useAuth();
  const entityId = user?.entity_identification;
  const [searchParams, setSearchParams] = useSearchParams();
  const openPartner = searchParams.get("partner");
  const [partners, setPartners] = useState<PartnerSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<ProgramFilter>("all");
  const [query, setQuery] = useState("");

  const load = async () => {
    if (!customer_identification || !entityId) return;
    setLoading(true);
    try {
      setPartners(
        await PartnerAdminService.list(customer_identification, entityId)
      );
    } catch (error: any) {
      toast.error(
        error?.response?.data?.message || "Failed to load the partners"
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customer_identification, entityId]);

  const shown = useMemo(() => {
    const q = query.trim().toLowerCase();
    return partners
      .filter((p) => filter === "all" || p.program === filter)
      .filter(
        (p) =>
          !q ||
          [p.first_name, p.last_name, p.email, p.company_name, p.partner_id]
            .filter(Boolean)
            .some((value) => String(value).toLowerCase().includes(q))
      )
      .sort((a, b) => {
        // An unreadable partner has no count: comparing it would return NaN, which leaves the order undefined.
        if (a.active_clients === null) return b.active_clients === null ? 0 : 1;
        if (b.active_clients === null) return -1;
        return b.active_clients - a.active_clients;
      });
  }, [partners, filter, query]);

  const distributors = partners.filter(
    (p) => p.program === "distributor"
  ).length;

  const setOpenPartner = (id: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (id) next.set("partner", id);
    else next.delete("partner");
    setSearchParams(next, { replace: !id });
  };

  return (
    <div className="partners-page">
      <Helmet>
        <title>Partners | Super User Manager</title>
      </Helmet>
      <div className="container mx-auto py-6">
        <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-xl font-semibold text-gray-900">Partners</h1>
            <p className="mt-0.5 text-sm text-gray-500">
              Affiliates and distributors, their contract and their clients
            </p>
          </div>
          <div className="flex items-center gap-2">
            <CustomButton
              variant="secondary"
              icon={<HiRefresh size={18} />}
              title="Refresh"
              disabled={loading}
              onClick={load}
            />
          </div>
        </div>

        <section className="mb-4 rounded-lg border border-gray-200 bg-white p-4">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end">
            <div className="flex-1">
              <label
                htmlFor="partner-search"
                className="mb-2 block text-sm font-medium text-gray-900"
              >
                Search partners (name, email, company or ID)
              </label>
              <input
                id="partner-search"
                className={FIELD}
                placeholder="Search by name, email, company or ID"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
              />
            </div>
            <div className="lg:w-72">
              <label
                htmlFor="partner-program"
                className="mb-2 block text-sm font-medium text-gray-900"
              >
                Program
              </label>
              <select
                id="partner-program"
                className={FIELD}
                value={filter}
                onChange={(e) => setFilter(e.target.value as ProgramFilter)}
              >
                {FILTERS.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </section>

        <UpgradeSettlements
          customerId={customer_identification}
          entityId={entityId}
          onOpenPartner={setOpenPartner}
        />

        <PartnerLinkFailures
          customerId={customer_identification}
          entityId={entityId}
          partners={partners}
          onOpenPartner={setOpenPartner}
          onChanged={load}
        />

        <PartnerDemoRequests
          customerId={customer_identification}
          entityId={entityId}
          partners={partners}
          onOpenPartner={setOpenPartner}
        />

        {loading && partners.length === 0 ? (
          <div className="flex items-center justify-center py-16">
            <Spinner size="xl" />
          </div>
        ) : (
          <section className="rounded-lg border border-gray-200 bg-white p-4">
            <div className="mb-4 flex flex-wrap gap-4 text-sm text-gray-500">
              <span>
                Total partners{" "}
                <span className="font-medium tabular-nums text-gray-900">
                  {partners.length}
                </span>
              </span>
              <span>
                Distributors{" "}
                <span className="font-medium tabular-nums text-gray-900">
                  {distributors}
                </span>
              </span>
            </div>
            <DataTable wrapperClassName="border-0">
              <DataTable.Head>
                <DataTable.HeadCell>Partner</DataTable.HeadCell>
                <DataTable.HeadCell>Company</DataTable.HeadCell>
                <DataTable.HeadCell>Program</DataTable.HeadCell>
                <DataTable.HeadCell>Contract signed</DataTable.HeadCell>
                <DataTable.HeadCell numeric>Clients</DataTable.HeadCell>
                <DataTable.HeadCell numeric>Active</DataTable.HeadCell>
                <DataTable.HeadCell>Milestone</DataTable.HeadCell>
              </DataTable.Head>
              <DataTable.Body>
                {shown.length === 0 ? (
                  <DataTable.Row>
                    <DataTable.Cell colSpan={7}>
                      <EmptyState
                        icon={<HiUserGroup size={24} />}
                        title="No partners found"
                      />
                    </DataTable.Cell>
                  </DataTable.Row>
                ) : (
                  shown.map((p) => (
                    <DataTable.Row
                      key={p.partner_id}
                      className="cursor-pointer transition-colors"
                      onClick={() => setOpenPartner(p.partner_id)}
                      title="Click to view details"
                    >
                      <DataTable.Cell className="whitespace-nowrap">
                        <div className="font-medium text-gray-900">
                          {partnerName(p)}
                        </div>
                        <div className="text-xs text-gray-400">
                          <NoTranslate>{p.email || p.partner_id}</NoTranslate>
                        </div>
                      </DataTable.Cell>
                      <DataTable.Cell>
                        {p.program === "unreadable"
                          ? "Unknown"
                          : p.company_name || "None"}
                      </DataTable.Cell>
                      <DataTable.Cell>{programBadge(p.program)}</DataTable.Cell>
                      <DataTable.Cell className="whitespace-nowrap text-xs tabular-nums text-gray-600">
                        {p.program === "unreadable"
                          ? "Unknown"
                          : formatDay(p.contract_signed_at)}
                      </DataTable.Cell>
                      <DataTable.Cell
                        numeric
                        className="tabular-nums text-gray-900"
                      >
                        {clientCount(p.attached_clients)}
                      </DataTable.Cell>
                      <DataTable.Cell
                        numeric
                        className="tabular-nums text-gray-900"
                      >
                        {clientCount(p.active_clients)}
                      </DataTable.Cell>
                      <DataTable.Cell className="text-xs text-gray-600">
                        {p.program === "unreadable"
                          ? "Unknown"
                          : p.program === "distributor"
                          ? p.milestone
                            ? `${p.milestone} reached`
                            : "Below 10"
                          : "Not applicable"}
                      </DataTable.Cell>
                    </DataTable.Row>
                  ))
                )}
              </DataTable.Body>
            </DataTable>
          </section>
        )}
      </div>

      <PartnerDrawer
        partnerId={openPartner}
        customerId={customer_identification}
        entityId={entityId}
        onClose={() => setOpenPartner(null)}
        onChanged={load}
      />
    </div>
  );
};

export default Partners;
